import { useEffect, useState } from 'react';
import { apiClient } from '@/services/api';
import type { TranslationEntry, SyncedLanguage } from '@/types/api';

interface TranslationsEditorProps {
  restaurantId: number;
  translations: TranslationEntry[];
  onChange: (translations: TranslationEntry[]) => void;
}

/** Generates a 16-char ID matching POS GlobalIdGenerator format */
function generateTranslationId(): string {
  let id = String(Date.now());
  while (id.length < 16) {
    id = String.fromCharCode(97 + Math.floor(Math.random() * 26)) + id;
  }
  return id.slice(0, 16);
}

export function TranslationsEditor({ restaurantId, translations, onChange }: TranslationsEditorProps) {
  const [languages, setLanguages] = useState<SyncedLanguage[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!restaurantId) return;
    setLoading(true);
    apiClient.getSyncedLanguages(restaurantId)
      .then((data) => setLanguages(data.filter((l) => l.isEnabled)))
      .catch(() => setLanguages([]))
      .finally(() => setLoading(false));
  }, [restaurantId]);

  if (loading) {
    return <p className="text-sm text-gray-400">Loading languages...</p>;
  }

  if (languages.length === 0) {
    return null;
  }

  const handleTextChange = (languageId: string, text: string) => {
    const existing = translations.find((t) => t.languageId === languageId);
    if (existing) {
      onChange(translations.map((t) =>
        t.languageId === languageId ? { ...t, text } : t
      ));
    } else {
      onChange([...translations, { id: generateTranslationId(), text, languageId }]);
    }
  };

  const handleRemove = (languageId: string) => {
    onChange(translations.filter((t) => t.languageId !== languageId));
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">Translations</label>
      <div className="space-y-2">
        {languages.map((lang) => {
          const entry = translations.find((t) => t.languageId === lang.posLanguageId);
          return (
            <div key={lang.id} className="flex items-center gap-2">
              <span className="text-sm text-gray-600 w-24 shrink-0">{lang.name}</span>
              <input
                type="text"
                value={entry?.text || ''}
                onChange={(e) => handleTextChange(lang.posLanguageId!, e.target.value)}
                placeholder={`Translation in ${lang.name}`}
                className="input flex-1"
              />
              {entry && (
                <button
                  type="button"
                  onClick={() => handleRemove(lang.posLanguageId!)}
                  className="text-xs text-gray-400 hover:text-gray-600 underline"
                >
                  Clear
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
